import { useState, useMemo } from 'react';

/**
 * Generic hook for managing compliance filtering across different compliance dashboards
 * Provides consistent filtering behavior and state management
 */
export interface FilterDefinition<T> {
  key: string;
  label: string;
  description: string;
  filter: (items: T[]) => T[];
}

export interface ComplianceFilteringOptions<T, F extends string> {
  data: T[];
  filters: Record<F, FilterDefinition<T>>;
  defaultFilter?: F;
  transformDataForDisplay?: (item: T) => T;
}

export const useComplianceFiltering = <T, F extends string>({
  data,
  filters,
  defaultFilter,
  transformDataForDisplay
}: ComplianceFilteringOptions<T, F>) => {
  const [activeFilter, setActiveFilter] = useState<F | 'none'>(defaultFilter || 'none');
  const [showTable, setShowTable] = useState(false);

  const filteredData = useMemo(() => {
    if (!data || activeFilter === 'none') {
      return transformDataForDisplay 
        ? data?.map(transformDataForDisplay) || []
        : data || [];
    }

    const filterDef = filters[activeFilter];
    if (!filterDef) {
      return transformDataForDisplay 
        ? data?.map(transformDataForDisplay) || []
        : data || [];
    }

    const filtered = filterDef.filter(data);
    return transformDataForDisplay 
      ? filtered.map(transformDataForDisplay)
      : filtered;
  }, [data, activeFilter, filters, transformDataForDisplay]);

  const getFilterDescription = () => {
    if (activeFilter === 'none') {
      return 'All Items';
    }
    
    const filterDef = filters[activeFilter];
    return filterDef ? filterDef.description : 'All Items';
  };

  const handleFilterChange = (filterKey: F | 'none') => {
    if (activeFilter === filterKey) {
      // If clicking the same filter, toggle table visibility
      setShowTable(!showTable);
    } else {
      // If clicking a different filter, show table with new filter
      setActiveFilter(filterKey);
      setShowTable(true);
    }
  };

  const getFilterCounts = () => {
    const counts: Record<string, number> = { none: data?.length || 0 };
    
    Object.keys(filters).forEach(key => {
      const filterDef = filters[key as F];
      counts[key] = data ? filterDef.filter(data).length : 0;
    });
    
    return counts;
  };

  return {
    activeFilter,
    showTable,
    filteredData,
    filterCounts: getFilterCounts(),
    handleFilterChange,
    getFilterDescription,
    setShowTable,
    setActiveFilter
  };
};
